// ChannelDayMixModal.jsx
//
// Standalone modal for configuring per-day channel shares (analysis.
// model_channel_day_shares). Opened from an action button on each
// model row in Modelslistview.
//
// Table layout:
//   - Rows: each active channel (carries a direction: Outbound | Inbound)
//   - Columns: Monday..Sunday
//   - Cells: percentage input. Empty cell = 0%. Non-zero cells are
//     tinted with the channel's color so the schedule is glanceable.
//     When the model has baseline contacts loaded, each cell also shows
//     a live derived count (share × that day's total contacts) and, when
//     channel rate inputs exist, the Total Collections $ (Coll: $x) on the
//     same line — both recomputed live as the share is edited. The cell
//     tooltip carries the outreach $ and full collections breakdown.
//   - Bottom totals are split: an Outbound subtotal row, an Inbound
//     subtotal row, then a grand Total row. Each shows % and (when a
//     baseline exists) the derived contact count.
//
// The header surfaces the model's average daily contacts and average
// daily balance across the configured week.
//
// Totals can legitimately exceed 100% - that just means the same
// customer base is contacted from multiple channels the same day.

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Typography,
} from "@mui/material";

import {
  getForecastModel, getChannelDayShares, saveChannelDayShares, getModelBaseline,
} from "../../../services/forecast/forecastservices";

const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const AMBER  = "#D49422";
const PURPLE = "#6C5BB5";
const PINK   = "#C45B8C";

// Same palette order as BaselineDataView so channel colors stay
// consistent across the app.
const CHANNEL_COLORS = [BLUE, GREEN, PURPLE, AMBER, PINK];

const DAY_NAMES = [
  { num: 1, full: "Monday" },
  { num: 2, full: "Tuesday" },
  { num: 3, full: "Wednesday" },
  { num: 4, full: "Thursday" },
  { num: 5, full: "Friday" },
  { num: 6, full: "Saturday" },
  { num: 7, full: "Sunday" },
];

// hex -> rgba with the given alpha
const tint = (hex, alpha = 0.18) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ── Display formatters (match BaselineDataView conventions) ───────
const fmtCount = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return "0";
  return Math.round(num).toLocaleString();
};

const fmtCurrency = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000)     return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
};

// Bold inline emphasis for the assumptions blurb.
const B = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{children}</Box>
);

// Section label: channel BLUE, uppercase, small-caps spacing.
const Lbl = ({ children }) => (
  <Box component="span" sx={{
    fontWeight: 700, color: BLUE,
    textTransform: "uppercase", letterSpacing: "0.06em",
    fontSize: 10.5, mr: 0.5,
  }}>{children}</Box>
);


export default function ChannelDayMixModal({ open, modelId, modelName, onClose, onSaved }) {
  const [channels, setChannels] = useState([]);   // active channels: [{ id, name, direction }]
  const [shares,   setShares]   = useState([]);   // [{ channel_id, day_of_week, share_of_contacts }]

  // Per-day baseline totals aggregated across DPD buckets:
  //   { [day_of_week]: { contacts, balance } }
  // Read-only here - it's just the multiplier that turns a share% into a
  // contact count / dollar figure on the fly.
  const [dayContacts, setDayContacts] = useState({});

  // Per-unit-share collections-$ base, keyed `${channel_id}|${day_of_week}`.
  // Mirrors BaselineDataView's funnel per DPD bucket:
  //   collections% = 24hr Pay% + (RPC% × PTP of RPC% × PTP Kept%)
  //   base        = Σ_dpd collections%(channel,dpd) × delinquency$(dpd)
  // A cell's live Coll$ is then base × that cell's share. Built once on
  // load from the baseline's channel_metrics + model_contacts (read-only).
  const [collBaseMap, setCollBaseMap] = useState({});

  // Per-(channel, day) RPC-count base, keyed `${channel_id}|${day_of_week}`:
  //   base = Σ_dpd contacts_count(day,dpd) × rpc_rate(channel,day,dpd)
  // A cell's live RPC count is base × share; RPC% = that ÷ Attempted Outreach.
  const [rpcBaseMap, setRpcBaseMap] = useState({});
  // Capacity per (channel, day), keyed `${channel_id}|${day_of_week}` →
  // int | null. When set, the channel's outcome figures (RPC, Collections)
  // use capacity ÷ total unique accounts in place of the channel share.
  // Also shown under Collections in the cell when non-zero.
  const [capacityMap, setCapacityMap] = useState({});

  // Assumptions panel (Baseline / Capacity / OB Split) — collapsed by default.
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  // Model description, shown under the title in smaller text.
  const [modelDesc, setModelDesc] = useState("");

  // Outbound / Inbound channel groups — collapsed on load.
  const [outboundOpen, setOutboundOpen] = useState(false);
  const [inboundOpen,  setInboundOpen]  = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");


  // ── Load model + shares + baseline when opened ────────────────
  useEffect(() => {
    if (!open || !modelId) return;
    setError("");
    setLoading(true);

    Promise.all([
      getForecastModel(modelId),
      getChannelDayShares(modelId),
      // Baseline is non-essential to editing shares, so a failure here
      // shouldn't block the editor - we just won't show derived counts.
      getModelBaseline(modelId).catch(() => null),
    ])
      .then(([model, shareData, baseline]) => {
        const ch = (model?.channel_personas || [])
          .filter(p => p.is_active !== false)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(p => ({
            id:        p.id,
            name:      p.name,
            // Anything not explicitly Inbound groups under Outbound so
            // legacy rows (pre-direction) still total cleanly.
            direction: p.direction === "Inbound" ? "Inbound" : "Outbound",
          }));
        setChannels(ch);
        setModelDesc(model?.description || "");

        setShares((shareData || []).map(s => ({
          channel_id:        s.channel_id,
          day_of_week:       s.day_of_week,
          share_of_contacts: Number(s.share_of_contacts) || 0,
        })));

        // Attempted Outreach per (channel, day). Only keep non-null values;
        // an absent key reads as null → no RPC% shown for that cell.
        // Capacity per (channel, day). Keep only non-null values.
        const caps = {};
        (shareData || []).forEach(s => {
          if (s.capacity != null) {
            caps[`${s.channel_id}|${s.day_of_week}`] = Number(s.capacity);
          }
        });
        setCapacityMap(caps);

        // Aggregate contacts + balance per day across all DPD buckets.
        const byDay = {};
        (baseline?.model_contacts || []).forEach(r => {
          const dow = r.day_of_week;
          if (!byDay[dow]) byDay[dow] = { contacts: 0, balance: 0 };
          byDay[dow].contacts += Number(r.contacts_count)   || 0;
          byDay[dow].balance  += Number(r.contacts_balance) || 0;
        });
        setDayContacts(byDay);

        // Per-(day, DPD) delinquency$ — needed because collections% varies
        // by DPD bucket, so we can't fold it into the per-day balance total.
        const balByDayDpd = {};
        const cntByDayDpd = {};
        (baseline?.model_contacts || []).forEach(r => {
          balByDayDpd[`${r.day_of_week}|${r.dpd_bucket_id}`] = Number(r.contacts_balance) || 0;
          cntByDayDpd[`${r.day_of_week}|${r.dpd_bucket_id}`] = Number(r.contacts_count)   || 0;
        });

        // Collections-$ base per (channel, day), independent of share.
        // collections% = 24hr Pay% + RPC% × PTP of RPC% × PTP Kept%   (same
        // funnel as BaselineDataView.derive); base sums collections% × $ over
        // DPD buckets. Cell Coll$ = base × live share.
        const collBase = {};
        (baseline?.channel_metrics || []).forEach(r => {
          const bal        = balByDayDpd[`${r.day_of_week}|${r.dpd_bucket_id}`] || 0;
          const conv24     = Number(r.conversion_24hr_payment_rate) || 0;
          const rpc        = Number(r.rpc_rate)                     || 0;
          const ptpOfRpc   = Number(r.ptp_of_rpc_rate)              || 0;
          const kept       = Number(r.conversion_kept_rate)         || 0;
          const collPct    = conv24 + rpc * ptpOfRpc * kept;
          const key        = `${r.channel_id}|${r.day_of_week}`;
          collBase[key]    = (collBase[key] || 0) + collPct * bal;
        });
        setCollBaseMap(collBase);

        // RPC-count base per (channel, day): Σ_dpd contacts_count × rpc_rate.
        // Cell RPC count = base × share; RPC% = that ÷ Attempted Outreach.
        const rpcBase = {};
        (baseline?.channel_metrics || []).forEach(r => {
          const cnt = cntByDayDpd[`${r.day_of_week}|${r.dpd_bucket_id}`] || 0;
          const rpc = Number(r.rpc_rate) || 0;
          const key = `${r.channel_id}|${r.day_of_week}`;
          rpcBase[key] = (rpcBase[key] || 0) + rpc * cnt;
        });
        setRpcBaseMap(rpcBase);
      })
      .catch(e => {
        setError(`Failed to load: ${e?.response?.data?.error || e?.message}`);
      })
      .finally(() => setLoading(false));
  }, [open, modelId]);


  // ── Lookup / mutate cells ─────────────────────────────────────
  const getShare = (channel_id, dow) => {
    const s = shares.find(x => x.channel_id === channel_id && x.day_of_week === dow);
    return s ? s.share_of_contacts : 0;
  };

  // valuePercent comes in as 0-100 (what the user types). Stored as
  // 0-1 decimal in state and DB.
  const setShare = (channel_id, dow, valuePercent) => {
    let v = Number(valuePercent);
    if (Number.isNaN(v)) v = 0;
    v = Math.max(0, Math.min(100, v)) / 100;

    setShares(prev => {
      const existing = prev.find(x => x.channel_id === channel_id && x.day_of_week === dow);
      if (existing) {
        return prev.map(x =>
          (x.channel_id === channel_id && x.day_of_week === dow)
            ? { ...x, share_of_contacts: v } : x
        );
      }
      return [...prev, { channel_id, day_of_week: dow, share_of_contacts: v }];
    });
  };


  // ── Baseline-derived helpers ──────────────────────────────────
  const dayTotalContacts = (dow) => dayContacts[dow]?.contacts || 0;
  const dayTotalBalance  = (dow) => dayContacts[dow]?.balance  || 0;

  // Live count / $ for a single channel cell.
  const cellCount   = (channel_id, dow) => getShare(channel_id, dow) * dayTotalContacts(dow);
  const cellDollars = (channel_id, dow) => getShare(channel_id, dow) * dayTotalBalance(dow);

  // Capacity-adjusted effective share for OUTCOMES (RPC, Collections).
  // When a Capacity is set for (channel, day), outcomes use
  //   capacity ÷ total unique accounts (for the day)
  // in place of the channel share; otherwise they use the share itself.
  const outcomeShare = (channel_id, dow) => {
    const cap = capacityMap[`${channel_id}|${dow}`];
    const tot = dayTotalContacts(dow);
    if (cap != null && cap > 0 && tot > 0) return cap / tot;
    return getShare(channel_id, dow);
  };

  // Live Total Collections $ for a cell: base (collections% × delinquency$,
  // summed over DPD buckets) scaled by the capacity-adjusted reach.
  const cellCollections = (channel_id, dow) =>
    (collBaseMap[`${channel_id}|${dow}`] || 0) * outcomeShare(channel_id, dow);

  // Live RPC %: cell RPC ÷ cell Outreach (the share-based count). With a
  // Capacity set, RPC uses the capacity-adjusted reach, so the % reflects it.
  // Returns null when there's no Outreach (empty cell) to divide by.
  const cellRpcPct = (channel_id, dow) => {
    const outreach = cellCount(channel_id, dow);   // share × day total (assigned)
    if (outreach <= 0) return null;
    const rpc = (rpcBaseMap[`${channel_id}|${dow}`] || 0) * outcomeShare(channel_id, dow);
    return (rpc / outreach) * 100;
  };

  // Capacity for a cell (manual, per channel/day). null/0 → not shown.
  const cellCapacity = (channel_id, dow) => {
    const v = capacityMap[`${channel_id}|${dow}`];
    return v == null ? null : v;
  };

  // Weekly averages across days that actually have contacts configured.
  const weekAverages = useMemo(() => {
    const entries = DAY_NAMES
      .map(d => dayContacts[d.num])
      .filter(x => x && x.contacts > 0);
    if (entries.length === 0) return null;
    const c = entries.reduce((s, x) => s + x.contacts, 0) / entries.length;
    const b = entries.reduce((s, x) => s + x.balance,  0) / entries.length;
    return { contacts: c, balance: b, dayCount: entries.length };
  }, [dayContacts]);

  const hasBaseline = !!weekAverages;
  // Whether the baseline has channel rate inputs to derive collections from.
  const hasMetrics  = Object.keys(collBaseMap).length > 0;

  // Direction subsets (for the split totals).
  const outboundChannels = channels.filter(c => c.direction === "Outbound");
  const inboundChannels  = channels.filter(c => c.direction === "Inbound");

  // Stable per-channel color, keyed by position in the full channel list,
  // so a channel keeps its color no matter which group it renders under.
  const colorByChannelId = useMemo(() => {
    const m = {};
    channels.forEach((c, i) => { m[c.id] = CHANNEL_COLORS[i % CHANNEL_COLORS.length]; });
    return m;
  }, [channels]);


  // ── Save - sends the full 7-day × N-channel matrix ────────────
  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const payload = [];
      for (const c of channels) {
        for (const d of DAY_NAMES) {
          payload.push({
            channel_id:        c.id,
            day_of_week:       d.num,
            share_of_contacts: getShare(c.id, d.num),
          });
        }
      }
      await saveChannelDayShares(modelId, { shares: payload });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };


  // ── A totals row for a given subset of channels ───────────────
  // Used for the Outbound subtotal, Inbound subtotal, and grand Total.
  // ── A group header row for a direction (Outbound / Inbound) ───
  // Doubles as the always-visible subtotal: shows %, count·RPC and
  // Collections$ per day, and a chevron to expand/collapse its channels.
  const renderGroupHeader = (label, chs, { bg, accent, isGrand = false, open = false, onToggle }) => (
    <Box
      onClick={onToggle}
      sx={{
        display: "grid",
        gridTemplateColumns: "1.8fr repeat(7, 1fr)",
        px: 1.5, py: "10px", gap: 0.5,
        background: bg,
        borderTop: `1px solid ${accent}40`,
        alignItems: "center",
        cursor: onToggle ? "pointer" : "default",
        userSelect: "none",
        "&:hover": onToggle ? { filter: "brightness(0.985)" } : undefined,
      }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.75,
        fontSize: 12, fontWeight: 700, color: accent,
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        <Box component="span" sx={{
          fontSize: 9, width: 10, display: "inline-block",
          transform: open ? "rotate(0deg)" : "rotate(-90deg)",
          transition: "transform 0.15s ease",
        }}>▼</Box>
        {label}
      </Box>
      {DAY_NAMES.map(d => {
        const total = chs.reduce((s, c) => s + getShare(c.id, d.num), 0);
        const pct = Math.round(total * 100);
        const isOver = total > 1.01;
        const isEmpty = total === 0;
        const totalCount = total * dayTotalContacts(d.num);
        const totalColl  = chs.reduce(
          (s, c) => s + (collBaseMap[`${c.id}|${d.num}`] || 0) * outcomeShare(c.id, d.num), 0);
        // Aggregate RPC%: Σ RPC ÷ Σ Outreach across the subset's channels.
        // RPC uses the capacity-adjusted reach; Outreach is share-based.
        let outreachSum = 0, rpcCountSum = 0;
        chs.forEach(c => {
          outreachSum += getShare(c.id, d.num) * dayTotalContacts(d.num);
          rpcCountSum += (rpcBaseMap[`${c.id}|${d.num}`] || 0) * outcomeShare(c.id, d.num);
        });
        const totalRpcPct = outreachSum > 0 ? (rpcCountSum / outreachSum) * 100 : null;
        return (
          <Box key={d.num} sx={{ textAlign: "center" }}>
            <Box sx={{
              fontSize: 12.5,
              fontWeight: 700,
              color: isEmpty ? "text.disabled"
                  : isOver  ? AMBER
                  : (isGrand && pct === 100) ? GREEN
                  : accent,
            }}>
              {isEmpty ? "—" : `${pct}%`}
            </Box>
            {hasBaseline && !isEmpty && (
              <Box sx={{
                fontSize: 9.5, lineHeight: 1.1, mt: "1px",
                fontWeight: 600, color: isOver ? AMBER : "text.secondary",
              }}>
                {fmtCount(totalCount)}
                {totalRpcPct != null && (
                  <Box component="span" sx={{ color: accent, fontWeight: 600 }}>
                    {" · "}RPC: {totalRpcPct.toFixed(0)}%
                  </Box>
                )}
              </Box>
            )}
            {hasMetrics && !isEmpty && (
              <Box sx={{
                fontSize: 9, lineHeight: 1.1, mt: "1px",
                fontWeight: 500, color: "text.secondary",
              }}>
                Collections: {fmtCurrency(totalColl)}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );


  // ── A single channel's row of 7 day cells ─────────────────────
  const renderChannelRow = (ch) => {
    const color = colorByChannelId[ch.id] || BLUE;
    return (
      <Box key={ch.id} sx={{
        display: "grid",
        gridTemplateColumns: "1.8fr repeat(7, 1fr)",
        px: 1.5, py: "10px", gap: 0.5,
        borderBottom: "0.5px solid", borderColor: "divider",
        alignItems: "center",
        "&:last-of-type": { borderBottom: "none" },
      }}>
        {/* Channel name with color dot + direction tag */}
        <Box sx={{
          fontSize: 12.5, fontWeight: 600,
          display: "flex", alignItems: "center", gap: 1,
        }}>
          <Box component="span" sx={{
            width: 10, height: 10, borderRadius: "50%",
            bgcolor: color, display: "inline-block", flexShrink: 0,
          }} />
          {ch.name}
          <Box component="span" sx={{
            fontSize: 9.5, fontWeight: 600, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.04em",
            border: "0.5px solid", borderColor: "divider",
            borderRadius: "10px", px: "6px", py: "1px",
            bgcolor: "#fafafa",
          }}>
            {ch.direction === "Inbound" ? "In" : "Out"}
          </Box>
        </Box>

        {/* Day cells */}
        {DAY_NAMES.map(d => {
          const v = getShare(ch.id, d.num);
          const filled = v > 0;
          const displayValue = filled ? Math.round(v * 100) : "";
          const count = cellCount(ch.id, d.num);
          const coll  = cellCollections(ch.id, d.num);
          const rpcPct = cellRpcPct(ch.id, d.num);
          const cap = cellCapacity(ch.id, d.num);

          return (
            <Box key={d.num} sx={{
              background: filled ? tint(color, 0.22) : "#fafafa",
              border: filled ? `1px solid ${tint(color, 0.55)}` : "0.5px solid #e8e8e8",
              borderRadius: "4px",
              minHeight: hasBaseline ? "56px" : "30px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: "2px",
              transition: "background 120ms ease",
              "&:focus-within": {
                outline: `1.5px solid ${color}`,
                outlineOffset: "-1px",
              },
            }}>
              {/* Percentage input row */}
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <TextField
                  variant="standard"
                  size="small"
                  type="number"
                  value={displayValue}
                  onChange={e => setShare(ch.id, d.num, e.target.value)}
                  disabled={saving}
                  placeholder=""
                  inputProps={{
                    min: 0, max: 100, step: 1,
                    style: {
                      textAlign: "center", padding: "2px 0",
                      fontSize: 13, fontWeight: filled ? 600 : 400,
                      color: filled ? color : "#bbb",
                      MozAppearance: "textfield",
                    },
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiInput-root::before, & .MuiInput-root::after": { display: "none" },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                      WebkitAppearance: "none", margin: 0,
                    },
                  }}
                />
                {filled && (
                  <Box component="span" sx={{
                    fontSize: 11, color, fontWeight: 600, ml: "1px",
                  }}>%</Box>
                )}
              </Box>

              {/* Row 2: count · RPC%   Row 3: Collections $ (live) */}
              {hasBaseline && (
                <>
                  <Box
                    sx={{
                      textAlign: "center",
                      fontSize: 9, lineHeight: 1.15, mt: "1px",
                      fontWeight: filled ? 600 : 400,
                      color: filled ? color : "text.disabled",
                    }}
                    title={filled
                      ? `${fmtCount(count)} unique accounts · Outreach ${fmtCurrency(cellDollars(ch.id, d.num))}`
                        + (rpcPct != null ? ` · RPC ${rpcPct.toFixed(1)}%` : "")
                        + (hasMetrics ? ` · Collections ${fmtCurrency(coll)}` : "")
                        + (cap ? ` · Capacity ${fmtCount(cap)}` : "")
                      : "0 unique accounts"}
                  >
                    {filled ? fmtCount(count) : "—"}
                    {filled && rpcPct != null && (
                      <Box component="span" sx={{ color, fontWeight: 600 }}>
                        {" · "}RPC: {rpcPct.toFixed(0)}%
                      </Box>
                    )}
                  </Box>
                  {filled && hasMetrics && (
                    <Box sx={{
                      textAlign: "center",
                      fontSize: 9, lineHeight: 1.15,
                      fontWeight: 500, color: "text.secondary",
                    }}>
                      Collections: {fmtCurrency(coll)}
                    </Box>
                  )}
                  {filled && cap ? (
                    <Box sx={{
                      textAlign: "center",
                      fontSize: 9, lineHeight: 1.15,
                      fontWeight: 500, color: "text.secondary",
                    }}>
                      Capacity: {fmtCount(cap)}
                    </Box>
                  ) : null}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };


  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
              Channel Mix by Day{modelName ? ` — ${modelName}` : ""}
            </Typography>
            {modelDesc && (
              <Typography sx={{ fontSize: 11.5, fontWeight: 400, color: "text.secondary", mt: 0.25 }}>
                {modelDesc}
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1 }} />

          {weekAverages && (
            <Box
              sx={{ display: "flex", gap: 2.5, alignItems: "baseline" }}
              title={`Averaged over ${weekAverages.dayCount} day(s) with configured unique accounts`}
            >
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                Avg daily unique delinquent accounts:{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {fmtCount(weekAverages.contacts)}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                Avg daily delinquency$:{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {fmtCurrency(weekAverages.balance)}
                </Box>
              </Typography>
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Loading…</Typography>
          </Box>
        ) : channels.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            This model has no active channels. Add channels first via Edit Model.
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 1.5 }}>
              <Box
                onClick={() => setAssumptionsOpen(o => !o)}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.75,
                  cursor: "pointer", userSelect: "none",
                  "&:hover": { opacity: 0.85 },
                }}
              >
                <Box sx={{
                  fontSize: 9, color: "text.secondary", width: 10,
                  display: "inline-block",
                  transform: assumptionsOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.15s ease",
                }}>▼</Box>
                <Lbl>Assumptions</Lbl>
              </Box>

              {assumptionsOpen && (
                <Box sx={{ mt: 0.75 }}>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.75 }}>
                    <Lbl>Baseline:</Lbl> On average <B>50,000</B> unique accounts are uploaded to
                    Alvaria each day — <B>66%</B> current and <B>34%</B> delinquent (<B>17,000</B> unique
                    delinquent accounts).
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.75 }}>
                    <Lbl>Capacity:</Lbl> We assume <B>104</B> agents each handle <B>75</B> calls/day — about <B>7,800</B> calls/day,
                    or <B>202,800</B>/month. Of the <B>7,800</B> daily calls, <B>1,020</B> are inbound
                    and <B>6,780</B> are outbound.
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                    <Lbl>OB Split:</Lbl> ~<B>3,390</B> delinquent outbound calls/day (<B>50%</B> of outbound) — ~<B>20%</B> of <B>17,000</B> delinquent accounts, or ~<B>88,140</B>/month.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{
              border: "0.5px solid", borderColor: "divider",
              borderRadius: 1.5, overflow: "hidden",
            }}>
              {/* Header row */}
              <Box sx={{
                display: "grid",
                gridTemplateColumns: "1.8fr repeat(7, 1fr)",
                px: 1.5, py: 1.25, gap: 0.5,
                background: "#f5f5f5",
                borderBottom: "0.5px solid", borderColor: "divider",
                fontSize: 11.5, fontWeight: 600, color: "text.primary",
              }}>
                <Box>Channels</Box>
                {DAY_NAMES.map(d => (
                  <Box key={d.num} sx={{ textAlign: "center" }}>{d.full}</Box>
                ))}
              </Box>

              {/* Outbound group — header doubles as subtotal; collapsed on load */}
              {outboundChannels.length > 0 && renderGroupHeader("Outbound", outboundChannels, {
                bg: "#f3f8fd", accent: BLUE, open: outboundOpen,
                onToggle: () => setOutboundOpen(o => !o),
              })}
              {outboundOpen && outboundChannels.map(ch => renderChannelRow(ch))}

              {/* Inbound group — header doubles as subtotal; collapsed on load */}
              {inboundChannels.length > 0 && renderGroupHeader("Inbound", inboundChannels, {
                bg: "#f2faf6", accent: GREEN, open: inboundOpen,
                onToggle: () => setInboundOpen(o => !o),
              })}
              {inboundOpen && inboundChannels.map(ch => renderChannelRow(ch))}
            </Box>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: "none", fontSize: 13 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading || channels.length === 0}
          variant="contained"
          sx={{
            textTransform: "none", borderRadius: "20px", fontSize: 13,
            background: BLUE, "&:hover": { background: "#2A6FB0" },
            boxShadow: "none", px: 3,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}